/* eslint-env browser */
// Security dashboard › Settings wiki (wave 3, W2_Portal_Tables look): a
// read-only reference for every /settings key.
//
// Joins the static catalogue (./settingsCatalog.js: what each key does, its
// default, who reads it, where it is edited) with the LIVE value from the
// Redux settings slice, which is already loaded at sign-in — opening this
// tab costs no read. "Refresh" does one on-demand get() of /settings for
// values changed since this tab loaded (as before).
//
// Live keys with no catalogue row are listed under "Not documented" so the
// wiki cannot silently drift. Secret values (tokens, webhooks) are never
// shown — /settings is world-readable, so their presence is flagged.
import React, { useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ref, get } from 'firebase/database';
import { FirebaseContext } from 'common';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DataObjectRoundedIcon from '@mui/icons-material/DataObjectRounded';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { InlineMessage, showToast } from '../../components/feedback';
import { tokens as c } from '../../theme';
import { formatAddisDateTime, formatAddisTime } from '../../common/exportUtils';
import { DataTable, PageHeader, SearchField, DropChip, ToggleChip, StatusChip, ActiveFilters } from '../../components/modern';
import { SETTINGS_CATALOG, GROUPS, GROUP_INFO, isSecretKey } from './settingsCatalog';

const UNDOCUMENTED = 'Not documented';
const ALL_GROUPS = 'All';
const TIMESTAMP_KEY = /(Since|EndsAt|eventDate)$/;

const isSet = (v) => v !== undefined && v !== null && v !== '';

// One plain-text rendering, shared by the table cell and the Markdown copy.
const valueText = (key, value) => {
  if (!isSet(value)) return 'unset → default';
  if (isSecretKey(key)) return 'set (hidden)';
  if (typeof value === 'boolean') return value ? 'ON' : 'OFF';
  if (typeof value === 'object') return JSON.stringify(value);
  if (TIMESTAMP_KEY.test(key) && Number(value) > 1e12) {
    return `${value} (${formatAddisDateTime(Number(value))})`;
  }
  return String(value);
};

const codeSx = { fontSize: 13, fontWeight: 600, color: c.INK, overflowWrap: 'anywhere', bgcolor: 'transparent' };

export default function SettingsWiki() {
  const { t, i18n } = useTranslation();
  const { database } = useContext(FirebaseContext);
  const reduxSettings = useSelector((state) => state.settingsdata?.settings);

  const [fresh, setFresh] = useState(null); // live /settings after Refresh
  const [refreshing, setRefreshing] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState(ALL_GROUPS);
  const [onlySet, setOnlySet] = useState(false);
  const [copied, setCopied] = useState(false);

  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });
  const usedLabels = {
    S: L('wiki_used_server', 'Server'),
    M: L('wiki_used_mobile', 'Mobile'),
    W: L('wiki_used_portal', 'Portal'),
    C: L('wiki_used_shared', 'Shared'),
  };
  const groupName = (g) => (g === UNDOCUMENTED ? L('wiki_undocumented', 'Not documented') : g);

  const live = useMemo(() => fresh || reduxSettings || {}, [fresh, reduxSettings]);

  const refresh = async () => {
    if (!database) return;
    setRefreshing(true);
    try {
      const snap = await get(ref(database, 'settings'));
      setFresh(snap.val() || {});
      setRefreshedAt(new Date());
    } catch (e) {
      console.error('[SettingsWiki] refresh failed:', e && e.message);
      showToast.error(L('wiki_refresh_failed', 'Could not load the live values. Check the internet connection, then try again.'));
    }
    setRefreshing(false);
  };

  // Catalogue rows + one row per live key the catalogue lacks.
  const allRows = useMemo(() => {
    const known = new Set(SETTINGS_CATALOG.map((e) => e.key));
    const extra = Object.keys(live)
      .filter((k) => !known.has(k))
      .sort()
      .map((k) => ({ key: k, group: UNDOCUMENTED, desc: 'Present in /settings but not in the wiki catalogue (settingsCatalog.js).', def: '—', used: '', edit: '—' }));
    return [...SETTINGS_CATALOG, ...extra];
  }, [live]);

  const stats = useMemo(() => {
    const documented = SETTINGS_CATALOG.length;
    const setCount = SETTINGS_CATALOG.filter((e) => isSet(live[e.key])).length;
    const undocumented = allRows.length - documented;
    const exposedSecrets = allRows.filter((r) => isSecretKey(r.key) && isSet(live[r.key])).map((r) => r.key);
    return { documented, setCount, undocumented, exposedSecrets };
  }, [allRows, live]);

  const groupOrder = [...GROUPS, UNDOCUMENTED];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = allRows.filter((r) => {
      if (group !== ALL_GROUPS && r.group !== group) return false;
      if (onlySet && !isSet(live[r.key])) return false;
      if (!q) return true;
      return r.key.toLowerCase().includes(q) || r.desc.toLowerCase().includes(q) || r.group.toLowerCase().includes(q);
    });
    // In section order, the catalogue order inside each section.
    return groupOrder.reduce((out, g) => out.concat(rows.filter((r) => r.group === g)), []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows, live, query, group, onlySet]);

  const sections = useMemo(
    () => groupOrder.map((g) => ({ group: g, rows: filtered.filter((r) => r.group === g) })).filter((s) => s.rows.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtered]
  );

  const toMarkdown = () => {
    const esc = (s) => String(s).replace(/\|/g, '\\|').replace(/\n/g, ' ');
    const lines = ['# /settings reference', '', `Generated ${formatAddisDateTime(Date.now())} (Addis Ababa time)`, ''];
    sections.forEach(({ group: g, rows }) => {
      lines.push(`## ${g}`, '');
      if (GROUP_INFO[g]) lines.push(GROUP_INFO[g], '');
      lines.push('| Key | What it does | Current | Default | Used by | Edited in |', '|---|---|---|---|---|---|');
      rows.forEach((r) => {
        const used = (r.used || '').split('').map((ch) => ({ S: 'Server', M: 'Mobile', W: 'Portal', C: 'Shared' }[ch] || ch)).join(', ') || '—';
        lines.push(`| \`${r.key}\` | ${esc(r.desc)} | ${esc(valueText(r.key, live[r.key]))} | ${esc(r.def)} | ${used} | ${esc(r.edit)} |`);
      });
      lines.push('');
    });
    return lines.join('\n');
  };

  const copyMarkdown = async () => {
    try {
      await navigator.clipboard.writeText(toMarkdown());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('[SettingsWiki] copy failed:', e && e.message);
      showToast.error(L('wiki_copy_failed', 'Could not copy. Allow clipboard access for this page, then try again.'));
    }
  };

  const valueCell = (k, value) => {
    if (!isSet(value)) return <StatusChip tone="neutral" icon={<RemoveRoundedIcon />} label={L('wiki_unset', 'unset → default')} />;
    if (isSecretKey(k)) return <StatusChip tone="warning" icon={<LockOutlinedIcon />} label={L('wiki_secret_set', 'set (hidden)')} />;
    if (typeof value === 'boolean') {
      return value ? <StatusChip tone="success" icon={<CheckRoundedIcon />} label="ON" /> : <StatusChip tone="neutral" icon={<RemoveRoundedIcon />} label="OFF" />;
    }
    if (typeof value === 'object') {
      const n = Object.keys(value).length;
      return (
        <Tooltip title={<Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>{JSON.stringify(value, null, 2).slice(0, 1500)}</Box>}>
          <span>
            <StatusChip tone="info" icon={<DataObjectRoundedIcon />} label={L('wiki_object_keys', '{{count}} keys', { count: n, defaultValue_one: '{{count}} key', defaultValue_other: '{{count}} keys' })} />
          </span>
        </Tooltip>
      );
    }
    const text = valueText(k, value);
    const short = text.length > 60 ? `${text.slice(0, 57)}…` : text;
    return (
      <Tooltip title={text.length > 60 ? text : ''}>
        <Box component="code" sx={codeSx}>{short}</Box>
      </Tooltip>
    );
  };

  const usedChips = (used) => {
    if (!used) return <Box component="span" sx={{ color: c.TEXT_3 }}>{L('wiki_used_nothing', 'nothing')}</Box>;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {used.split('').map((ch) => (
          <Chip key={ch} size="small" variant="outlined" label={usedLabels[ch] || ch} sx={{ fontWeight: 600, borderColor: c.OUTLINE_STRONG, color: c.INK_2 }} />
        ))}
      </Box>
    );
  };

  const columns = [
    { title: L('wiki_col_key', 'Key'), field: 'key', width: '18%', render: (r) => <Box component="code" sx={codeSx}>{r.key}</Box> },
    { title: L('wiki_col_desc', 'What it does'), field: 'desc', width: '32%', sortable: false },
    { title: L('wiki_col_value', 'Current value'), id: 'value', width: '16%', sortable: false, searchable: false, render: (r) => valueCell(r.key, live[r.key]), exportValue: (r) => valueText(r.key, live[r.key]) },
    { title: L('wiki_col_default', 'Default'), field: 'def', width: '10%', sortable: false },
    { title: L('wiki_col_used', 'Used by'), field: 'used', width: '10%', sortable: false, render: (r) => usedChips(r.used) },
    { title: L('wiki_col_edit', 'Edited in'), field: 'edit', width: '12%', sortable: false },
    { title: L('wiki_col_section', 'Section'), field: 'group', render: (r) => groupName(r.group), exportValue: (r) => groupName(r.group) },
  ];

  const groupOptions = [{ value: ALL_GROUPS, label: L('wiki_all_sections', 'All sections') }, ...groupOrder.map((g) => ({ value: g, label: groupName(g) }))];
  const tokens = [];
  if (group !== ALL_GROUPS) tokens.push({ key: 'group', group: L('wiki_section', 'Section'), value: groupName(group) });
  if (onlySet) tokens.push({ key: 'set', group: L('wiki_filter', 'Filter'), value: L('wiki_only_set', 'Only keys set in RTDB') });
  if (query.trim()) tokens.push({ key: 'q', group: L('search', 'Search'), value: query.trim() });
  const removeToken = (key) => {
    if (key === 'group') setGroup(ALL_GROUPS);
    if (key === 'set') setOnlySet(false);
    if (key === 'q') setQuery('');
  };
  const clearAll = () => {
    setGroup(ALL_GROUPS);
    setOnlySet(false);
    setQuery('');
  };

  const title = L('wiki_title', 'Settings wiki');

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        title={title}
        icon={<MenuBookOutlinedIcon />}
        hue="info"
        secondaryActions={[
          { key: 'copy', label: copied ? L('wiki_copied', 'Copied') : L('wiki_copy_md', 'Copy as Markdown'), icon: copied ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon />, onClick: copyMarkdown },
          { key: 'refresh', label: refreshing ? L('fin_refreshing', 'Refreshing…') : L('refresh', 'Refresh'), icon: <RefreshRoundedIcon />, onClick: refresh, disabled: refreshing },
        ]}
      />
      <Typography variant="body2" sx={{ color: c.INK_2, mt: -1 }}>
        {L('wiki_intro', 'Every /settings key: what it does, its current value, the default used when it is unset, which part of the system reads it, and where to change it. Read-only.')}
      </Typography>

      {stats.exposedSecrets.length > 0 ? (
        <InlineMessage kind="warning" title={L('wiki_secrets_title', 'Credentials in a public node')}>
          {L('wiki_secrets_body', '/settings is world-readable (database rules .read: true), so these credentials are public to anyone with the Firebase config: {{keys}}. Move them to an admin-only node or Cloud Functions environment config.', { keys: stats.exposedSecrets.join(', ') })}
        </InlineMessage>
      ) : null}

      <Paper sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 1.5, columnGap: 3 }}>
        <SearchField value={query} onChange={setQuery} placeholder={L('wiki_search', 'Search key or description')} />
        <DropChip label={L('wiki_section', 'Section')} value={group} allValue={ALL_GROUPS} options={groupOptions} onChange={setGroup} />
        <ToggleChip label={L('wiki_only_set', 'Only keys set in RTDB')} count={stats.setCount} selected={onlySet} onClick={() => setOnlySet((v) => !v)} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, color: c.INK_2, fontSize: 13.5, flexBasis: '100%' }}>
          <span>{L('wiki_stat_documented', '{{n}} documented', { n: stats.documented })}</span>
          <span aria-hidden="true">·</span>
          <span>{L('wiki_stat_set', '{{n}} set in RTDB', { n: stats.setCount })}</span>
          <span aria-hidden="true">·</span>
          <span>{L('wiki_stat_defaults', '{{n}} using defaults', { n: stats.documented - stats.setCount })}</span>
          <span aria-hidden="true">·</span>
          <Box component="span" sx={{ color: stats.undocumented ? c.WARNING_TEXT : c.INK_2, fontWeight: stats.undocumented ? 600 : 400 }}>
            {L('wiki_stat_undocumented', '{{n}} not documented', { n: stats.undocumented })}
          </Box>
          <span aria-hidden="true">·</span>
          <span>{refreshedAt ? L('wiki_values_as_of', 'Live values as of {{time}}', { time: formatAddisTime(refreshedAt) }) : L('wiki_values_loaded', 'Values as loaded with this tab')}</span>
        </Box>
      </Paper>

      {group !== ALL_GROUPS && GROUP_INFO[group] ? (
        <Typography variant="body2" sx={{ color: c.INK_2 }}>{GROUP_INFO[group]}</Typography>
      ) : null}

      <DataTable
        title={title}
        rows={filtered}
        columns={columns}
        rowKey="key"
        search={false}
        searchText={query}
        onSearchChange={setQuery}
        serverSearch
        resortKey={`${refreshedAt ? refreshedAt.getTime() : 0}-${group}`}
        headerContent={tokens.length ? <ActiveFilters tokens={tokens} onRemove={removeToken} onClearAll={clearAll} /> : null}
        countText={L('wiki_count', '{{count}} keys', { count: filtered.length, defaultValue_one: '{{count}} key', defaultValue_other: '{{count}} keys' })}
        prefsKey="settings_wiki"
        pageSizeOptions={[50, 100, 250]}
        minWidth={980}
        emptyState={{ icon: <MenuBookOutlinedIcon />, title: L('wiki_empty', 'No keys match.'), actionLabel: tokens.length ? L('clear_filters', 'Clear filters') : undefined, onAction: tokens.length ? clearAll : undefined }}
      />
    </Box>
  );
}
